/**
 * AssetRadar — shared frontend types.
 *
 * THIS FILE IS THE FROZEN MIRROR OF THE BACKEND JSON CONTRACT.
 * Nothing in the SPA may re-declare a wire shape; import it from here.
 *
 * Where the backend definitions live:
 *   - Role / Permission / ROLE_PERMISSIONS ......... app/auth/permissions.py
 *     (this file must stay byte-for-byte value-identical to that matrix)
 *   - Auth + admin endpoints ....................... app/routes/auth.py,
 *                                                    app/routes/admin.py,
 *                                                    app/auth/service.py
 *   - Dashboard overview read model ................ app/services/dashboard_service.py
 *   - Triage / notification fan-out ................ app/services/vulnerability_event_service.py
 *   - CRA report drafting (notify) ................. app/services/meldung_service.py
 *   - CSV import plan + apply ...................... app/services/import_service.py
 *   - Scans / schema / assets / settings ........... app/routes/api.py,
 *                                                    app/services/kev_scan_service.py,
 *                                                    app/services/csaf_service.py,
 *                                                    app/services/schema_service.py,
 *                                                    app/services/orchestrator.py
 *
 * Conventions that apply to EVERY wire type below:
 *   1. NULLABILITY, NOT OPTIONALITY. The backend builds responses from Python
 *      dict literals over `card.get(...)`, so keys are ALWAYS present and empty
 *      values serialise as `null`. Model them `field: T | null`, never `field?:`.
 *      The only genuinely optional members in the whole surface are
 *      `BewertungResult.benachrichtigungen` and the `UpdateUserRequest` fields.
 *   2. TWO DATE CONTRACTS. `dashboard_service` normalises through `_iso_or_none`
 *      => `IsoDateTime | null`. `vulnerability_event_service` passes CMDBuild
 *      values through RAW => `CmdbDateTime` (ISO, 'dd/MM/yyyy HH:mm:ss',
 *      'yyyy-MM-dd', or epoch millis as a number). Always render via the
 *      defensive fmtDate helper, never `new Date(x).toLocaleString()` directly.
 *   3. German strings coming from the server (plan errors 'Zeile N: …', 404/409
 *      details, notify errors) are displayed VERBATIM — never re-translated.
 *   4. Numeric-looking CMDBuild "double" attributes can arrive as strings when
 *      the CSV import wrote them (see `CveFacts.base_score`).
 */

import { msg } from '@lingui/core/macro';

import { translatedRecord } from './locale';

/* ------------------------------------------------------------------------- *
 * 1. Roles, permissions and the RBAC matrix
 *
 * Naming rule (load-bearing — do not break it):
 *   `*.read`  => granted to the auditor role (safe, non-mutating, no infra
 *                disclosure). These are exactly the auditor's permissions.
 *   everything else (`*.write`, `*.run`, `*.send`, `*.admin`) => admin only.
 * Adding a role later = adding a row to ROLE_PERMISSIONS on both sides. Call
 * sites must ask for a Permission, never for a Role.
 * ------------------------------------------------------------------------- */

export const ROLES = ['admin', 'auditor'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  // --- read permissions (auditor + admin) ---
  'dashboard.read',
  'triage.read',
  'asset.read',
  'cmdb.read',
  'dt.read',
  // --- write / privileged permissions (admin only) ---
  'triage.write',
  'notification.send',
  'scan.run',
  'cmdb.write',
  'import.write',
  'schema.admin',
  'system.admin',
  'user.admin',
  'session.admin',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** The auditor's row: exactly the `*.read` permissions. */
export const AUDITOR_PERMISSIONS = [
  'dashboard.read',
  'triage.read',
  'asset.read',
  'cmdb.read',
  'dt.read',
] as const satisfies readonly Permission[];

/** The admin's row: every permission, listed explicitly (never a wildcard). */
export const ADMIN_PERMISSIONS = [
  'dashboard.read',
  'triage.read',
  'asset.read',
  'cmdb.read',
  'dt.read',
  'triage.write',
  'notification.send',
  'scan.run',
  'cmdb.write',
  'import.write',
  'schema.admin',
  'system.admin',
  'user.admin',
  'session.admin',
] as const satisfies readonly Permission[];

/**
 * The matrix, mirrored from `ROLE_PERMISSIONS` in app/auth/permissions.py.
 * Kept client-side so the admin UI can render the roles/permissions grid
 * without a round trip; `GET /api/admin/roles` remains authoritative.
 */
export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  admin: ADMIN_PERMISSIONS,
  auditor: AUDITOR_PERMISSIONS,
};

/** Labels for the admin UI (roles), translated on every read. */
export const ROLE_LABELS: Readonly<Record<Role, string>> = translatedRecord<Role>({
  admin: msg`Administrator`,
  auditor: msg`Auditor (nur lesen)`,
});

/** Descriptions for the admin UI (roles), translated on every read. */
export const ROLE_DESCRIPTIONS: Readonly<Record<Role, string>> = translatedRecord<Role>({
  admin: msg`Vollzugriff: Stammdaten, Bewertungen, Benachrichtigungen, Scans, Import, Schema, Benutzerverwaltung.`,
  auditor: msg`Nur lesender Zugriff auf Dashboard, Bewertungs-Warteschlange, Stammdaten, Dependency-Track-Befunde und Asset-Bestand.`,
});

/** Descriptions for the admin UI (permissions), translated on every read. */
export const PERMISSION_DESCRIPTIONS: Readonly<Record<Permission, string>> = translatedRecord<Permission>({
  'dashboard.read': msg`Produkt-/Kundenübersicht mit KEV-Verdikt lesen`,
  'triage.read': msg`Bewertungs-Warteschlange und CVE-Exploitability lesen`,
  'asset.read': msg`Asset-Bestand und SBOM-Upload-Historie lesen`,
  'cmdb.read': msg`Stammdaten (Kunden, Produkte, Versionen, Instanzen) lesen`,
  'dt.read': msg`Dependency-Track-Befunde und SBOM je Instanz lesen`,
  'cmdb.write': msg`Stammdaten anlegen, ändern und löschen`,
  'triage.write': msg`CVE-Bewertung (Betroffen / Nicht betroffen) erfassen`,
  'notification.send': msg`Benachrichtigungen erzeugen, entwerfen und als versendet stempeln`,
  'scan.run': msg`CSAF-Sync, Rescan und SBOM-Export ausführen`,
  'import.write': msg`CSV-Import planen und anwenden (AssetRadar und xDome)`,
  'schema.admin': msg`CMDBuild-Schema anlegen, aufräumen und Daten löschen`,
  'system.admin': msg`Systemkonfiguration und Legacy-Oberflächen einsehen`,
  'user.admin': msg`Benutzer und Rollen verwalten`,
  'session.admin': msg`Aktive Sitzungen einsehen (Audit)`,
});

/** Union of the permissions granted by a set of roles. Pure, no I/O. */
export function permissionsForRoles(roles: readonly Role[]): Permission[] {
  const out = new Set<Permission>();
  for (const role of roles) {
    for (const perm of ROLE_PERMISSIONS[role] ?? []) out.add(perm);
  }
  return PERMISSIONS.filter((p) => out.has(p));
}

/** Narrowing guard for values arriving from the server. */
export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

/** Narrowing guard for values arriving from the server. */
export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------------- *
 * 2. Shared scalars, unions and the error envelope
 * ------------------------------------------------------------------------- */

/** ISO-8601 timestamp produced by the backend (`_iso_or_none`). */
export type IsoDateTime = string;

/**
 * A date/time passed through from CMDBuild WITHOUT normalisation.
 * May be ISO-8601, 'dd/MM/yyyy HH:mm:ss', 'yyyy-MM-dd' or epoch millis.
 * Only `vulnerability_event_service` emits these.
 */
export type CmdbDateTime = string | number | null;

/** Envelope status used by every mutating endpoint. 'partial' => `errors` non-empty. */
export type ApiStatus = 'ok' | 'partial';

/** Lookup `Benachrichtigung - Status`. Anything other than OFFEN counts as done. */
/** QUEUED, SENDING and FAILED are the mail queue (Postausgang, DEV-93): frozen, not sent yet. */
export type BenachrichtigungStatus =
  | 'OFFEN' | 'QUEUED' | 'SENDING' | 'FAILED' | 'VERSENDET' | 'GEMELDET' | 'ABGESCHLOSSEN';

/** Lookup `VulnerabilityEvent - Bewertung`. BEHOBEN is set by the KEV scan only. */
export type Bewertung = 'OFFEN' | 'UNDER_INVESTIGATION' | 'BETROFFEN' | 'NICHT_BETROFFEN' | 'BEHOBEN';

/* Bottom-up assessment (DEV-100): one status vocabulary on all levels. */

/** VEX status of a component, product version or instance. FIXED: product level only. */
export type AssessmentStatus = 'OPEN' | 'UNDER_INVESTIGATION' | 'AFFECTED' | 'NOT_AFFECTED' | 'FIXED';

/** VEX justification catalog; ENVIRONMENT is valid on instances only. */
export type VexJustification =
  | 'COMPONENT_NOT_PRESENT'
  | 'VULNERABLE_CODE_NOT_PRESENT'
  | 'VULNERABLE_CODE_NOT_IN_EXECUTE_PATH'
  | 'VULNERABLE_CODE_CANNOT_BE_CONTROLLED_BY_ADVERSARY'
  | 'INLINE_MITIGATIONS_ALREADY_EXIST'
  | 'ENVIRONMENT';

export type AssessmentPriority = 'HIGH' | 'MEDIUM' | 'LOW';

/** One entry of a FastAPI/pydantic 422 validation body. */
export interface ValidationErrorItem {
  loc: Array<string | number>;
  msg: string;
  type: string;
}

/**
 * FastAPI error bodies come in two shapes: `{detail: string}` (HTTPException)
 * and `{detail: ValidationErrorItem[]}` (pydantic 422). `lib/api.ts` must
 * normalise both — plus the empty-body case (204) — into `ApiError.detail`.
 */
export interface ApiErrorBody {
  detail: string | ValidationErrorItem[];
}

/** Visual tone shared by KpiTile / Chip / Banner. */
export type Tone = 'neutral' | 'good' | 'warn' | 'alert';

/** KPI tiles add Kritisch (CVSS 9.0–10 colour) for the dot, e.g. KEV-CVEs. */
export type KpiTone = Tone | 'critical';

/** Props-level shape for the KPI tiles (six on the dashboard, five on import). */
export interface KpiCounter {
  label: string;
  value: number;
  tone: KpiTone;
  title?: string;
}

/* ------------------------------------------------------------------------- *
 * 3. Authentication — /api/auth/*
 * ------------------------------------------------------------------------- */

/** `GET /api/auth/me` → 200. A 401 here is the normal unauthenticated case. */
export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  roles: Role[];
  permissions: Permission[];
  must_change_password: boolean;
  /** Runtime presentation variant; it never changes server-side permissions. */
  app_variant: 'standard' | 'simple' | 'demo';
  /** Separate safety gate for the destructive CMDB demo restore. */
  demo_reset_enabled: boolean;
}

/** `POST /api/auth/login` request. */
export interface LoginRequest {
  email: string;
  password: string;
}

/** `POST /api/auth/login` → 200 (+ Set-Cookie craw_session). 401 bad creds, 429 locked. */
export interface LoginResponse {
  user: AuthUser;
}

/** `POST /api/auth/password` request (self-service). Answers 204. */
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

/** Shape exposed by `useAuth()` in lib/auth.tsx. */
export interface AuthState {
  user: AuthUser | null;
  /** true while the initial `GET /api/auth/me` is in flight. */
  loading: boolean;
  /** Non-null only for hard failures (network / 5xx) — never for a plain 401. */
  error: string | null;
  login: (body: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  changePassword: (body: ChangePasswordRequest) => Promise<void>;
  has: (permission: Permission) => boolean;
  hasAny: (...permissions: Permission[]) => boolean;
}

/* ------------------------------------------------------------------------- *
 * 4. Administration — /api/admin/*
 * ------------------------------------------------------------------------- */

/** Row of `GET /api/admin/users`. */
export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  roles: Role[];
  active: boolean;
  must_change_password: boolean;
  created_at: IsoDateTime;
  last_login_at: IsoDateTime | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

/** `POST /api/admin/users` → 201. 409 duplicate e-mail, 422 weak password. */
export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  roles: Role[];
  active: boolean;
}

/**
 * `PATCH /api/admin/users/{id}`. The ONLY request type in this file with
 * genuinely optional members — send only what changed.
 * 409 when the change would remove the last active admin.
 */
export interface UpdateUserRequest {
  name?: string;
  roles?: Role[];
  active?: boolean;
  password?: string;
  must_change_password?: boolean;
}

/** Response of both POST and PATCH on /api/admin/users. */
export interface UserResponse {
  user: AdminUser;
}

/** Row of `GET /api/admin/roles` — the server-side truth for the matrix. */
export interface RoleInfo {
  name: Role;
  description: string;
  permissions: Permission[];
}

export interface AdminRolesResponse {
  roles: RoleInfo[];
}

/** Row of `GET /api/admin/sessions`. Token hashes are never returned. */
export interface SessionInfo {
  id: string;
  user_id: string;
  user_email: string;
  created_at: IsoDateTime;
  last_seen_at: IsoDateTime;
  /** Sliding expiry (session_ttl_minutes). */
  expires_at: IsoDateTime;
  /** Hard cap (session_absolute_ttl_hours). */
  absolute_expires_at: IsoDateTime;
  ip: string | null;
  user_agent: string | null;
}

export interface AdminSessionsResponse {
  sessions: SessionInfo[];
}

/* ------------------------------------------------------------------------- *
 * 5. Dashboard overview — GET /api/dashboard/overview?only_affected=
 *    All timestamps here are normalised ISO-8601 or null.
 * ------------------------------------------------------------------------- */

/** A CRA report record attached to one KomponenteVersion (`_meldung_block`). */
export interface MeldungBlock {
  id: number | null;
  /** Titel ?? Description */
  titel: string | null;
  /** No default here — may be null. */
  status: BenachrichtigungStatus | null;
  erstellt_am: IsoDateTime | null;
}

/** One KomponenteVersion inside a product row (`_komponente_block`). */
export interface KomponenteBlock {
  id: number | null;
  /** Bezeichnung ?? Description ?? Product ?? Code */
  name: string | null;
  /** Version ?? FirmwareVersion */
  version: string | null;
  /** Hersteller ?? Vendor */
  hersteller: string | null;
  /** KevBetroffen OR kev_cves.length > 0 */
  kev_betroffen: boolean;
  /** Upper-cased CVE ids, split on ',' and ';'. */
  kev_cves: string[];
  letzter_kev_scan: IsoDateTime | null;
  meldungen: MeldungBlock[];
  gemeldet: boolean;
}

/** Per-customer notification record (`_benachrichtigung_block`). */
export interface BenachrichtigungBlock {
  id: number | null;
  /** Defaults to 'OFFEN' server-side. */
  status: BenachrichtigungStatus;
  /** EmailEmpfaenger */
  empfaenger: string | null;
  versendet_am: IsoDateTime | null;
}

/** The delivered ProduktInstanz. */
export interface OverviewProdukt {
  id: number;
  /** Seriennummer ?? Code */
  seriennummer: string | null;
  /** Description */
  bezeichnung: string | null;
}

/** The bought ProduktType (key name kept stable: `produkttyp`). */
export interface OverviewProduktTyp {
  id: number | null;
  /** Bezeichnung ?? Code */
  bezeichnung: string | null;
  modellreihe: string | null;
}

/** The ProduktVersion the instance currently runs (the known configuration). */
export interface OverviewProduktVersion {
  id: number | null;
  version_nr: string | null;
  /** Description ?? Code */
  name: string | null;
}

export interface OverviewKunde {
  id: number | null;
  /** Name ?? Code */
  name: string | null;
  /** AnsprechpartnerEmail */
  email: string | null;
  /** IsEu of the customer's country (DEV-49); false without a country. */
  ist_eu: boolean;
}

/** One row of the overview table — one ProduktInstanz. */
export interface OverviewRow {
  produkt: OverviewProdukt;
  produkttyp: OverviewProduktTyp;
  produktversion: OverviewProduktVersion;
  kunde: OverviewKunde;
  /** Sorted: affected first, then name ascending. */
  komponenten: KomponenteBlock[];
  affected_count: number;
  /** Sorted unique union over the affected components. */
  kev_cves: string[];
  /** Flattened over `komponenten`. */
  meldungen: MeldungBlock[];
  benachrichtigungen: BenachrichtigungBlock[];
  /** >= 1 Benachrichtigung AND none still 'OFFEN'. */
  notified: boolean;
}

/** Drives the six dashboard KPI tiles (plus `offen` from the events payload). */
export interface DashboardSummary {
  products: number;
  products_affected: number;
  components_affected: number;
  kev_cves: number;
  customers_affected: number;
  affected_not_notified: number;
}

export interface DashboardOverviewResponse {
  generated_at: IsoDateTime;
  summary: DashboardSummary;
  count: number;
  /** Sorted: -affected_count, notified ascending, kunde.name ascending. */
  rows: OverviewRow[];
}

/** Query for the overview — the ONLY dashboard filter that hits the server. */
export interface DashboardOverviewQuery {
  only_affected: boolean;
}

/* ------------------------------------------------------------------------- *
 * 6. Triage queue — GET /api/dashboard/vulnerability-events
 *    WARNING: every date in this section is a raw CmdbDateTime.
 * ------------------------------------------------------------------------- */

/** Notification record as seen from an event (`_benachrichtigung_block`). */
export interface EventBenachrichtigung {
  id: number | null;
  /** Defaults to 'OFFEN' server-side. */
  status: BenachrichtigungStatus;
  empfaenger: string | null;
  erstellt_am: CmdbDateTime;
  versendet_am: CmdbDateTime;
}

export interface EventKunde {
  id: number | null;
  name: string | null;
  email: string | null;
}

/** One ProduktInstanz affected by an event. */
export interface BetroffeneInstanz {
  instanz_id: number | null;
  /** Seriennummer ?? Code */
  seriennummer: string | null;
  /** ProduktType.Bezeichnung (key name kept stable). */
  produkttyp: string | null;
  /** ProduktVersion.VersionNr of the CURRENT version. */
  produktversion: string | null;
  kunde: EventKunde;
  /** null while no Benachrichtigung card exists yet. */
  benachrichtigung: EventBenachrichtigung | null;
}

export interface CveFacts {
  /** CveId of the event, upper-cased; falls back to the CVE card. */
  id: string | null;
  beschreibung: string | null;
  /** CMDBuild 'double' — arrives as a string when written by the CSV import. */
  base_score: number | string | null;
  aktiv_ausgenutzt: boolean;
  /** DEV-131, maintained by the KEV scan: vector and version of the same rating as `base_score`. */
  cvss_vector: string | null;
  cvss_version: string | null;
  /** When the CVE was published (NVD). */
  published_at: CmdbDateTime | null;
  /** ENISA EU Vulnerability Database id and its public page. */
  euvd_id: string | null;
  euvd_url: string | null;
}

/** Native Dependency-Track 5.1 KEV assertion details for one event. */
export interface KevFacts {
  /** DT vulnerability title, falling back to the KEV source display name. */
  name: string | null;
  /** Description exposed by the preferred assertion. */
  beschreibung: string | null;
  /** Assertion publication date, 'YYYY-MM-DD'. */
  aufgenommen_am: string | null;
  /** Kept for API compatibility; DT 5.1 does not publicly expose dueDate. */
  frist: string | null;
  /** Normalized native known_ransomware value. */
  ransomware: 'Known' | 'Not known' | null;
  /** Native required_action. */
  empfohlene_massnahme: string | null;
  /** Enabled KEV sources asserting this vulnerability. */
  sources: Array<string | null>;
}

export interface EventKomponente {
  id: number | null;
  /** Bezeichnung */
  name: string | null;
  /** Version ?? FirmwareVersion */
  version: string | null;
  hersteller: string | null;
}

export interface EventProduktVersion {
  id: number | null;
  /** Description ?? Code */
  name: string | null;
  version_nr: string | null;
}

/** Saved evidence files of a product assessment; the advisory lives on the case (DEV-134). */
export interface TriageDocumentSummary {
  attachments: number;
}

/** Security Advisory of a vulnerability case (DEV-134); null until a template was saved. */
export type AdvisoryStatus = 'IN_PROGRESS' | 'RELEASED';

/** One row of the triage table. */
export interface VulnerabilityEventRow {
  id: number;
  /** Defaults to 'OFFEN' server-side. */
  bewertung: Bewertung;
  begruendung: string | null;
  bewertet_von: string | null;
  bewertet_am: CmdbDateTime;
  erstellt_am: CmdbDateTime;
  cve: CveFacts;
  kev: KevFacts;
  komponente: EventKomponente;
  /** Sorted by name. */
  produktversionen: EventProduktVersion[];
  betroffene: BetroffeneInstanz[];
  /** Distinct kunde.id count. */
  betroffene_kunden: number;
  benachrichtigungen_offen: number;
  benachrichtigungen_versendet: number;
  /** Progress per level; null when the assessments could not be read. */
  assessment?: EventAssessmentSummary | null;
}

/** Assessment progress of one triage row (component × CVE) per level. */
export interface EventAssessmentSummary {
  component_status: AssessmentStatus;
  products: {
    total: number; confirmed: number; affected: number; needs_review: number; contradictions: number;
    /** Confirmed AFFECTED versions without a released Security Advisory (DEV-129). */
    advisory_open: number;
  };
  /** Instances follow their product version; only deviations are work (DEV-130). */
  instances: { total: number; deviations: number; needs_review: number; version_changed: number };
  /** The KEV scan closed the open hit (no longer in the field / CVE gone). */
  closed_by_scan: boolean;
}

/** Fields of a stored product or instance verdict; all null while only proposed. */
export interface StoredAssessment {
  id: number | null;
  status: AssessmentStatus | null;
  justification: VexJustification | null;
  comment: string | null;
  assessed_by: string | null;
  assessed_at: CmdbDateTime;
  needs_review: boolean;
  review_reason: string | null;
}

export interface ComponentAssessment {
  event_id: number;
  component_version_id: number | null;
  name: string | null;
  version: string | null;
  manufacturer: string | null;
  status: AssessmentStatus;
  justification: VexJustification | null;
  comment: string | null;
  assessed_by: string | null;
  assessed_at: CmdbDateTime;
  closed_by_scan: boolean;
}

export interface InstanceAssessmentRow extends StoredAssessment {
  instance_id: number;
  serial: string | null;
  customer: string | null;
  version_id: number;
  /** The product's effective verdict. */
  proposal: AssessmentStatus;
  effective_status: AssessmentStatus;
  confirmed: boolean;
  deviates: boolean;
  /** Assessed on another version than the one the instance runs now. */
  version_changed: boolean;
  /** No own verdict: the instance follows the confirmed product verdict (DEV-130). */
  inherited: boolean;
  priority: AssessmentPriority | null;
  network_connected: boolean;
  remote_access: boolean;
  compensating_controls: boolean;
  environment_notes: string | null;
  /** Reason for a deviating NOT_AFFECTED, worded for the customer's mail (DEV-128). */
  customer_notice: string | null;
}

export interface ProductAssessmentRow extends StoredAssessment {
  version_id: number;
  product_type_id: number | null;
  /** The product's vulnerability case; its customer mails cover every affected version (DEV-134). */
  case_id: number | null;
  advisory_revision: number | null;
  product_name: string | null;
  version_nr: string | null;
  version_code: string | null;
  custom: boolean;
  component_version_ids: number[];
  /** Suggested by the component verdicts. */
  proposal: AssessmentStatus;
  effective_status: AssessmentStatus;
  confirmed: boolean;
  deviates: boolean;
  instance_count: number;
  notifications: { total: number; sent: number };
  /** Customers were notified "affected", the product says otherwise now. */
  contradiction: boolean;
  /** null when the document store was unavailable. */
  documents?: TriageDocumentSummary | null;
  /** The advisory of the product's case (DEV-134). */
  advisory_status: AdvisoryStatus | null;
  instances: InstanceAssessmentRow[];
}

/** One product version × CVE whose customers are (to be) notified. */
/** A version of a notification row: affected now, or named in a mail of the case. */
export interface NotificationQueueVersion {
  version_id: number;
  version_nr: string | null;
  version_code: string | null;
  custom: boolean;
  /** Confirmed AFFECTED now. */
  affected: boolean;
}

/** One vulnerability case (product × CVE) whose customers are (to be) notified (DEV-134). */
export interface NotificationQueueRow {
  /** Its advisory is the mail text. */
  case_id: number;
  product_type_id: number;
  product_name: string | null;
  cve: string;
  versions: NotificationQueueVersion[];
  component_version_ids: number[];
  components: string[];
  instance_count: number;
  customer_count: number;
  /** Names of the customers running an affected version, sorted. */
  customers: string[];
  /** open = not queued yet; queued = waiting or being sent; failed = sending failed (DEV-93). */
  notifications: { total: number; open: number; queued: number; failed: number; sent: number };
  /** Next unreported CRA deadline of the product's vulnerability case (DEV-132). */
  next_deadline: IsoDateTime | null;
  /** Notified, but no version is AFFECTED any more. */
  contradiction: boolean;
  /** Mails are drafted and queued only from a RELEASED case advisory (DEV-134). */
  advisory_status: AdvisoryStatus | null;
  advisory_revision: number | null;
}

/** `GET /api/triage/notifications`. */
export interface NotificationQueueResponse {
  count: number;
  rows: NotificationQueueRow[];
}

/** `GET /api/triage/events/{id}` — the three levels of one hit. */
export interface EventAssessmentDetail {
  event_id: number;
  cve: string;
  component: ComponentAssessment;
  products: ProductAssessmentRow[];
  /** The vulnerability cases of the products above this hit (DEV-132). */
  cases: VulnerabilityCase[];
}

/* --- Vulnerability case per product × CVE (DEV-132) ----------------------- */

export type CaseStatus = 'OPEN' | 'COMPLETED' | 'OBSOLETE';
export type ReportStage = 'early_warning' | 'notification' | 'final_report';
export type CaseSensitivity = 'CLEAR' | 'GREEN' | 'AMBER' | 'AMBER_STRICT' | 'RED';
/** `waiting`: the final report before a corrective measure is recorded. */
export type CaseClockState = 'open' | 'due_soon' | 'overdue' | 'submitted' | 'waiting';

/** Core facts, entered once: authority report, advisory and customer mail share them. */
export interface CaseFacts {
  summary: string;
  exploitation_details: string;
  impact: string;
  user_mitigations: string;
  corrective_measures: string;
}

/** What only the authority report needs. */
export interface CaseAuthorityFields {
  sensitivity: CaseSensitivity | null;
  special_circumstances: boolean;
  special_circumstances_note: string;
  threat_actor_info: string;
}

export interface CaseClock {
  key: '24h' | '72h' | '14d';
  stage: ReportStage;
  label: string;
  due: IsoDateTime | null;
  state: CaseClockState;
  /** Until the deadline; negative once overdue, null when reported or waiting. */
  remaining_hours: number | null;
  submitted_at: IsoDateTime | null;
  submitted_by: string | null;
  on_time: boolean | null;
  /** The reported content was kept with the stamp (DEV-60). */
  content_saved: boolean;
}

/** Where a report field is maintained, for the hint on a missing value (DEV-60). */
export type ReportFieldSource = 'case' | 'psirt' | 'cmdb' | 'assessment';

export interface AuthorityReportField {
  key: string;
  label: string;
  source: ReportFieldSource;
  /** False for „soweit bekannt“ fields. */
  required: boolean;
  value: string;
  missing: boolean;
}

/** `GET /api/triage/cases/{id}/reports/{stage}` — prepared from the case, or as recorded (DEV-60). */
export interface AuthorityReport {
  case_id: number;
  stage: ReportStage;
  label: string;
  submitted_at: IsoDateTime | null;
  submitted_by: string | null;
  /** The fields are the reported content, frozen when the report was recorded. */
  saved: boolean;
  prepared_at: IsoDateTime | null;
  /** The case says something else by now than what was reported. */
  changed_since: boolean;
  /** In the order of the SRP form. */
  fields: AuthorityReportField[];
  /** Labels of required fields without a value. */
  missing: string[];
  /** Every field as plain text, for „Alles kopieren“ and the download. */
  text: string;
}

export interface CaseStage {
  stage: ReportStage;
  label: string;
  /** Labels of the inputs still missing — a hint, never a lock. */
  missing: string[];
}

export interface CaseVersion {
  version_id: number;
  version_nr: string | null;
  custom: boolean;
  status: AssessmentStatus;
  confirmed: boolean;
  instance_count: number;
}

export interface VulnerabilityCase {
  id: number;
  product_type_id: number | null;
  product_name: string | null;
  cve: string;
  status: CaseStatus;
  /** Reported to the authority, but no version is affected any more. */
  contradiction: boolean;
  aware_since: IsoDateTime | null;
  facts: CaseFacts;
  authority: CaseAuthorityFields;
  facts_updated_by: string | null;
  facts_updated_at: IsoDateTime | null;
  corrective_measure: { available_at: IsoDateTime | null; recorded_by: string | null };
  srp_reference: string;
  /** The advisory in brief; the full one on its own route (DEV-134). */
  advisory: { status: AdvisoryStatus | null; revision: number; first_released_at: IsoDateTime | null };
  /** Customer mails of the case: the step after the released advisory (DEV-103). */
  mails: { total: number; sent: number };
  clocks: CaseClock[];
  next_deadline: IsoDateTime | null;
  stages: CaseStage[];
  versions: CaseVersion[];
  member_states: Array<{ iso: string; name: string }>;
  customers_without_country: string[];
}

export type CaseFactsUpdate = Partial<CaseFacts & CaseAuthorityFields>;

/* --- Deadline timeline, page „Meldefristen“ (DEV-137) ------------------- */

/** A vulnerability case today; serious incidents (Art. 14(3), DEV-62) join later. */
export type TimelineKind = 'vulnerability';

export type TimelineMilestoneKind = 'hit' | 'aware' | 'report' | 'corrective' | 'mail' | 'advisory' | 'event';

export interface TimelineMilestone {
  kind: TimelineMilestoneKind;
  at: IsoDateTime;
  label: string;
  /** report: submitted before its deadline. */
  on_time?: boolean | null;
  /** event: STARTED / DUE_SOON / OVERDUE. */
  event_type?: DeadlineEventType;
}

export interface TimelineCase extends VulnerabilityCase {
  kind: TimelineKind;
  /** The first KEV hit of the CVE in this product: the initial assessment starts here. */
  first_hit_at: IsoDateTime | null;
  /** From the first hit to awareness X (or now). */
  triage_hours: number | null;
  /** A triage row of this case, for the deep link into the dialog. */
  event_id: number | null;
  mails: { total: number; sent: number; first_sent_at: IsoDateTime | null; last_sent_at: IsoDateTime | null };
  /** Every release of the case advisory with its change note (DEV-134). */
  advisories: Array<{ version: string | null; at: IsoDateTime | null; note: string | null }>;
  deadline_events: Array<{ type: DeadlineEventType; clock: string; label: string; at: IsoDateTime | null; due: IsoDateTime | null }>;
  /** Everything that happened, oldest first. */
  milestones: TimelineMilestone[];
}

/** A KEV hit whose product versions are still undecided: no case, no deadline yet. */
export interface TimelineTriageRow {
  kind: TimelineKind;
  event_id: number;
  cve: string;
  component: string | null;
  component_status: AssessmentStatus;
  products: string[];
  since: IsoDateTime | null;
  age_hours: number | null;
}

/** `GET /api/triage/deadline-timeline`. */
export interface DeadlineTimelineResponse {
  generated_at: IsoDateTime;
  warn_hours: number;
  summary: { overdue: number; due_24h: number; waiting_corrective: number; triage_open: number };
  /** Overdue first, then by next deadline; completed and obsolete cases last. */
  cases: TimelineCase[];
  /** Oldest first. */
  triage: TimelineTriageRow[];
}

/** `GET/PATCH /settings/reporting`. */
export interface ReportingSettings {
  deadline_warn_hours: number;
  default_warn_hours: number;
  min_warn_hours: number;
  max_warn_hours: number;
}

/* --- Customer branding, login screen and page title (DEV-182) --- */

export type BrandingLogoVariant = 'light' | 'dark';

export interface BrandingLogo {
  /** Same-origin URL with the content hash (`?v=…`), so a new logo gets a new URL. */
  url: string;
  width: number;
  height: number;
}

/** `GET /api/branding` — public: the login screen needs it before anyone is signed in. */
export interface Branding {
  /** Plain text under the logo (≤ 3 lines); rendered as text, never as HTML. */
  text: string;
  title_suffix: string;
  /** "AssetRadar" or "AssetRadar – <title_suffix>". */
  page_title: string;
  logos: Record<BrandingLogoVariant, BrandingLogo | null>;
  limits: {
    text_max_chars: number;
    text_max_lines: number;
    title_suffix_max_chars: number;
    logo_max_bytes: number;
    logo_max_side: number;
    logo_types: string[];
  };
}

/* --- Notification dispatch, System → Benachrichtigungsversand (DEV-126) --- */

export type DispatchSettingType = 'integer' | 'number' | 'text' | 'secret' | 'boolean' | 'choice';

/** One setting of a transport or a global rule of the dispatch job. */
export interface DispatchSetting {
  key: string;
  label: string;
  type: DispatchSettingType;
  default: number | string | boolean | null;
  minimum: number | null;
  maximum: number | null;
  help: string;
  /** Must be filled before the transport can be activated. */
  required: boolean;
  /** `choice` only. */
  options: Array<{ value: string; label: string }>;
  /** Absent for secrets: they are never handed back, only `configured`. */
  value?: number | string | boolean | null;
  configured?: boolean;
}

export interface MailTransportInfo {
  key: string;
  label: string;
  description: string;
  /** False for „Manuell“: no dispatch job runs. */
  automatic: boolean;
  unavailable_reason: string | null;
  active: boolean;
  can_test: boolean;
  /** Sends nothing; its records are marked as simulated (demo, DEV-127). */
  simulated: boolean;
  /** Required settings still empty: cannot be activated yet. */
  missing: string[];
  settings: DispatchSetting[];
}

/** `GET/PUT /settings/mail-dispatch`. */
export interface MailDispatchSettings {
  active_transport: string;
  global: DispatchSetting[];
  transports: MailTransportInfo[];
}

export type DispatchValue = number | string | boolean | null;

export interface MailDispatchUpdate {
  active_transport: string;
  global: Record<string, DispatchValue>;
  transports: Record<string, Record<string, DispatchValue>>;
}

export interface MailDispatchRun {
  at: IsoDateTime;
  transport: string;
  sent: number;
  retry: number;
  failed: number;
  skipped: number;
  /** Automatic attempts that never finished: failed for a person to check. */
  interrupted: number;
  error: string | null;
}

/** `GET /settings/mail-dispatch/status`. */
export interface MailDispatchStatus {
  active_transport: string;
  queued: number;
  failed: number;
  /** Queued, but no version is affected any more: a person decides. */
  waiting_for_decision: number;
  last_run: MailDispatchRun | null;
  next_run: IsoDateTime | null;
}

export interface VerdictRequest {
  status: AssessmentStatus;
  justification?: VexJustification | null;
  comment?: string;
}

export interface InstanceVerdictRequest extends VerdictRequest {
  priority?: AssessmentPriority | null;
  network_connected?: boolean;
  remote_access?: boolean;
  compensating_controls?: boolean;
  environment_notes?: string;
  /** Only stored with NOT_AFFECTED; the internal comment never reaches the customer. */
  customer_notice?: string;
}

/** `POST /api/triage/events/{id}/adopt` — the component verdict for the product versions (DEV-130). */
export interface AdoptResult {
  status: AssessmentStatus;
  dry_run: boolean;
  adopted: Array<{ version_id: number; label: string; notifications?: { created: number } | null }>;
  /** Product versions left alone, with the German reason. */
  skipped: Array<{ version_id: number; label: string; reason: string }>;
}

export interface ComponentVerdictResult {
  event_id: number;
  status: AssessmentStatus;
  marked_for_review: number;
}

export interface ProductVerdictResult {
  assessment_id: number;
  version_id: number;
  cve: string;
  status: AssessmentStatus;
  marked_for_review: number;
  /** Present for AFFECTED: the customer notifications of the version's instances. */
  notifications?: { created: number; existing: number; errors: string[] };
  /** Present otherwise: open notifications removed, nothing had been sent. */
  notifications_removed?: number;
}

export interface InstanceVerdictResult {
  assessment_id: number;
  instance_id: number;
  cve: string;
  status: AssessmentStatus;
}

/**
 * The triage payload is allowed to FAIL SOFT: the VulnerabilityEvent /
 * Benachrichtigung classes may not be provisioned on a fresh CMDBuild, so the
 * dashboard falls back to `{count: 0, offen: 0, events: []}`.
 */
export interface VulnerabilityEventsResponse {
  count: number;
  /**
   * Events with anything left to assess on any level (DEV-136): component open
   * or under investigation, an unconfirmed product version, or a verdict to
   * review. Feeds the 'Offene Bewertungen' KPI of triage and dashboard.
   */
  offen: number;
  /** Sorted: open ones first (see `offen`), then id descending. */
  events: VulnerabilityEventRow[];
}

/** Where a placeholder's value comes from (DEV-134). */
export type AdvisorySource = 'fact' | 'cmdb' | 'table' | 'release' | 'field' | 'notes';

export interface AdvisoryReleaseEntry {
  version: string;
  revision: number;
  released_at: IsoDateTime;
  released_by: string | null;
  note: string;
}

/**
 * `GET /api/triage/cases/{id}/advisory` — the Security Advisory of a case
 * (DEV-134), rendered from the one template (DEV-138), core facts, CMDB and
 * further fields.
 */
export interface CaseAdvisory {
  case_id: number;
  cve: string;
  /** The one template (Markdown), for the live preview of unsaved edits. */
  template: string;
  revision: number;
  status: AdvisoryStatus | null;
  /** Further fields by placeholder; a value overrides the CMDB. */
  extras: Record<string, string>;
  /** Section „Weitere Hinweise“. */
  notes: string;
  /** Every placeholder: label, source and the value the case supplies. */
  fields: Record<string, { label: string; source: AdvisorySource; value: string }>;
  /** The saved advisory, rendered. */
  text: string;
  /** Placeholders of the saved advisory still empty: they block the release. */
  missing: Array<{ name: string; label: string; source: AdvisorySource }>;
  updated_at: IsoDateTime | null;
  updated_by: string | null;
  released_at: IsoDateTime | null;
  released_by: string | null;
  /** The publication date: stays when the advisory is released again. */
  first_released_at: IsoDateTime | null;
  change_log: AdvisoryReleaseEntry[];
  /** false for an obsolete case. */
  writable: boolean;
  /** The customer mails of the case; they are queued from the released advisory (DEV-103). */
  mails: { total: number; sent: number };
}

/** `GET /settings/advisory-template` — the one template of every advisory (DEV-138). */
export interface AdvisoryTemplateSettings {
  markdown: string;
  /** The built-in template is in use. */
  is_default: boolean;
  default_markdown: string;
  updated_at: IsoDateTime | null;
  updated_by: string | null;
  /** Every placeholder a template may use, with an example value for the preview. */
  placeholders: Array<{ name: string; label: string; source: AdvisorySource; example: string }>;
}

export interface CaseAdvisoryUpdate {
  values: Record<string, string>;
  notes: string;
  revision: number;
}

export interface TriageAttachment {
  id: string;
  filename: string;
  size: number;
  content_type: string;
  uploaded_at: string;
  uploaded_by: string;
}

export interface TriageDocuments {
  attachments: TriageAttachment[];
  limits: { max_file_size: number; max_files: number; allowed_extensions: string[] };
}

/* ------------------------------------------------------------------------- *
 * 7. Notifications and e-mail drafts (MVP: nothing is ever really sent)
 * ------------------------------------------------------------------------- */

/** One instance named in a customer notification. */
export interface DraftInstanz {
  instanz_id: number | null;
  seriennummer: string | null;
  produkttyp: string | null;
  produktversion: string | null;
  /** The verdict as the mail states it: "betroffen" or "derzeit nicht betroffen…". */
  hinweis?: string | null;
  /** AFFECTED or NOT_AFFECTED — frozen at sending for a sent notification. */
  reported_status: AssessmentStatus | null;
  customer_notice: string | null;
}

/**
 * One draft per Benachrichtigung — one mail to one customer for this product
 * version × CVE (DEV-128), sorted by kunde.name. A customer may have a sent
 * notification and a follow-up (`benachrichtigung.previous_id`) for
 * instances that turned up afterwards.
 */
export interface EmailDraft {
  benachrichtigung: EventBenachrichtigung & { previous_id: number | null };
  kunde: { id: number; name: string | null; email: string | null };
  instanzen: DraftInstanz[];
  betreff: string;
  /** Plain text, '\n'-joined German advisory — render in a wrapping <pre>. */
  inhalt: string;
  /** null when the customer has no e-mail on file. */
  mailto: string | null;
  /**
   * advisory: from the saved Security Advisory; saved: the operator's saved draft;
   * missing: no advisory, no draft; queued: frozen in the Postausgang; sent: the text stored when sent.
   */
  source: 'advisory' | 'saved' | 'missing' | 'queued' | 'sent';
  /** The frozen HTML mail of a queued or sent notification (null otherwise). */
  html: string | null;
  /** A saved draft whose instance list has changed since — save again or discard (DEV-93). */
  stale: boolean;
  saved_at?: CmdbDateTime;
  saved_by?: string | null;
  /** Template placeholders ({{…}}) still in the text. */
  placeholders: number;
}

export interface EmailDraftsResult {
  /** Upper-cased CveId, '' when the card has none. */
  cve: string;
  product_type_id: number | null;
  /** The case; drafts come only from its RELEASED advisory (DEV-134). */
  case_id: number | null;
  advisory_status: AdvisoryStatus | null;
  advisory_revision: number | null;
  drafts: EmailDraft[];
}

/**
 * `POST /api/triage/cases/{id}/email` — no body (DEV-134).
 * MUTATING despite the name: it re-runs the Benachrichtigung fan-out first.
 * 409 with a German detail unless a version of the product is a confirmed AFFECTED.
 */
export type EmailDraftsResponse = EmailDraftsResult;

/** `POST /api/dashboard/benachrichtigungen/{id}/versendet` — a body IS required. */
/** `POST /api/triage/notifications/queue` — queue drafts in the Postausgang (DEV-93). */
export interface QueueRequest {
  /** The vulnerability case the drafts belong to (DEV-134). */
  case_id: number;
  /**
   * What gets frozen: the subject, the Markdown draft (the plain-text
   * alternative) and its HTML rendering — the mail itself.
   */
  items: Array<{ id: number; betreff: string; inhalt: string; html: string }>;
}

export interface QueueResult {
  queued: number[];
  /** Drafts that could not be queued, with the German reason. */
  errors: Array<{ id: number; detail: string }>;
}

/** One queued, sending or failed notification in the Postausgang — or, on request, a sent one. */
export interface OutboxRow {
  id: number;
  status: BenachrichtigungStatus;
  /** The vulnerability case the mail informs about (DEV-134). */
  case_id: number | null;
  product_name: string | null;
  /** The versions the mail names its instances with, comma-separated. */
  version_nr: string | null;
  cve: string | null;
  kunde: { id: number | null; name: string | null };
  empfaenger: string | null;
  betreff: string;
  /** The frozen Markdown (plain-text alternative). */
  inhalt: string;
  /** The frozen mail as HTML; null for notifications queued before HTML mails. */
  html: string | null;
  instance_count: number;
  queued_at: CmdbDateTime;
  queued_by: string | null;
  send_started_at: CmdbDateTime;
  send_started_by: string | null;
  send_attempts: number;
  last_error: string | null;
  /** DEV-126: after a temporary failure, the earliest next automatic attempt. */
  next_attempt_at: IsoDateTime | null;
  /** Name of the transport from the register; simulated: nothing was really sent (DEV-127). */
  transport_label: string | null;
  transport_simulated: boolean;
  versendet_am: CmdbDateTime;
  /** `manual` today; automatic transports later (DEV-125). */
  transport: string | null;
  message_id: string | null;
  /** Who made the last status change — for a sent mail: who confirmed it. */
  status_changed_by: string | null;
  advisory_revision: number | null;
  /** The Security Advisory was saved again since the mail was queued. */
  advisory_changed: boolean;
  previous_id: number | null;
  /** The product verdict is no longer AFFECTED: not sent until someone decides. */
  contradiction: boolean;
  override: boolean;
}

/** `GET /api/triage/notifications/outbox`. */
export interface OutboxResponse {
  count: number;
  rows: OutboxRow[];
}

export type OutboxAction = 'withdraw' | 'begin' | 'release' | 'confirm' | 'override' | 'requeue';

/* ------------------------------------------------------------------------- *
 * 8. CSV import — plan (preview) and apply (merge review)
 * ------------------------------------------------------------------------- */

/** The eight classes of the import schema, in `_CLASS_ORDER`. */
export type ImportKlass =
  | 'Kunde'
  | 'ProduktType'
  | 'CVE'
  | 'ProduktVersion'
  | 'KomponenteType'
  | 'KomponenteVersion'
  | 'ProduktVersionKomponente'
  | 'ProduktInstanz';

/**
 * Widened class name for the RENDERING path: a class the server adds or renames
 * must never silently vanish from the review — unknown values are grouped last
 * under their raw name.
 */
export type ImportKlassLoose = ImportKlass | (string & {});

/** Fixed rendering order for the plan blocks; headings in IMPORT_KLASS_LABELS. */
export const IMPORT_KLASS_ORDER = [
  'Kunde',
  'ProduktType',
  'CVE',
  'ProduktVersion',
  'KomponenteType',
  'KomponenteVersion',
  'ProduktVersionKomponente',
  'ProduktInstanz',
] as const satisfies readonly ImportKlass[];

export const IMPORT_KLASS_LABELS: Readonly<Record<ImportKlass, string>> = translatedRecord<ImportKlass>({
  Kunde: msg`Kunden`,
  ProduktType: msg`Produkte`,
  CVE: msg`CVEs`,
  ProduktVersion: msg`Produktversionen`,
  KomponenteType: msg`Komponenten`,
  KomponenteVersion: msg`Komponentenversionen`,
  ProduktVersionKomponente: msg`Stücklisten`,
  ProduktInstanz: msg`Produktinstanzen (Auslieferungen)`,
});

/** 'remove' = a Stücklisteneintrag the uploaded SBOM lacks (target uploads only). */
export type PlanAction = 'create' | 'update' | 'unchanged' | 'remove';

/**
 * Per-entry merge decision. Absent entries default to 'incoming' SERVER-SIDE,
 * except 'remove' entries, which are kept ('existing') unless decided otherwise.
 * `match:<key>` assigns an ambiguous component to that existing catalog entry.
 */
export type ImportDecision = 'incoming' | 'existing' | `match:${string}`;

/** One field-level diff line. */
export interface PlanChange {
  /** Attribute name, or a reference attribute such as 'ProduktTypeID'. */
  field: string;
  /** null on create and when the previous value was empty. */
  old: string | null;
  /** Always a string: scalars normalised, references as 'Klass »key«'. */
  new: string;
}

/** Display metadata is separate from the stable keys used for merge decisions. */
export interface PlanDisplayReference {
  key: string;
  name: string;
  version?: string;
}

/** An existing catalog entry an ambiguous scanned component may stand for. */
export interface PlanCandidate {
  key: string;
  name: string;
  version?: string | null;
  vendor?: string | null;
}

export interface PlanEntryDisplay {
  name: string;
  version?: string;
  product?: PlanDisplayReference;
  component?: PlanDisplayReference;
  /** `${klass}:${key}` -> target; includes old targets absent from this import. */
  references?: Record<string, PlanDisplayReference>;
  /** Scanned component matched to an existing catalog entry (SBOM imports). */
  match?: { from: string; by: 'purl' | 'cpe' | 'fields' };
  /** Several catalog entries fit — the entry needs an explicit decision. */
  candidates?: PlanCandidate[];
  /** Instance upload: component versions this scanned version replaces. */
  replaces?: string[];
  /** Instance upload: a current component the scan replaced by another version. */
  replaced_by_scan?: boolean;
  /** 'remove' entries: the component is in the Stückliste but not in the SBOM. */
  missing_in_sbom?: boolean;
  /**
   * Instance upload: a new catalog entry without a decision of its own. It is
   * created exactly when one of these Stückliste entries is taken.
   */
  follows?: string[];
}

export interface PlanEntry {
  /** '{klass}:{key}' — the key used in the apply decisions map. */
  entry_id: string;
  klass: ImportKlassLoose;
  /** Natural key, e.g. 'Gateway X:V3' or 'SN-0001~Firmware'. */
  key: string;
  action: PlanAction;
  /** Empty for 'unchanged'. */
  changes: PlanChange[];
  /** null on 'create'. */
  existing_id: number | null;
  /** Optional for compatibility with existing/older import plans. */
  display?: PlanEntryDisplay;
}

export interface ImportPlan {
  /** uuid4 hex — the plan expires after 30 minutes (in-process store). */
  plan_id: string;
  total_rows: number;
  creates: number;
  updates: number;
  unchanged: number;
  /**
   * Class name -> number of CMDB cards the CSV does not mention (imports never
   * delete). All eight `IMPORT_KLASS_ORDER` keys are always present, value 0
   * when nothing is untouched; typed loosely so a new class still sums up.
   */
  untouched_in_cmdb: Record<string, number>;
  /** Per-row German parse errors ('Zeile 7: …'); the plan stays usable. */
  errors: string[];
  entries: PlanEntry[];
  /** Stücklisteneinträge the SBOM lacks (target uploads only). */
  removes?: number;
  /** entry_ids that apply refuses without an explicit decision. */
  requires_decision?: string[];
  /** Present when the SBOM was uploaded onto a chosen product version. */
  target?: ImportTarget;
}

/** Upload onto one product (DEV-107): new official version or update of one. */
export interface ProductImportTarget {
  kind: 'product';
  mode: 'new' | 'update';
  product: { id: number; name: string };
  version: { id: number | null; key: string; version_nr: string };
  /** Instances currently running the updated version. */
  affected_instances: number;
  /** Versions of the product whose Stückliste equals the scanned configuration. */
  equals_versions: { id: number; version_nr: string; custom: boolean }[];
}

/**
 * Upload of one delivered instance's scan (DEV-107). The plan proposes the
 * outcome for the default decisions; the server recomputes it on apply.
 */
export interface InstanceImportTarget {
  kind: 'instance';
  instance: { id: number; serial: string; customer: string };
  product: { id: number; key: string; name: string };
  current_version: { id: number; key: string; version_nr: string; custom: boolean };
  /** Official version a custom version would be based on. */
  base_version: { id: number; version_nr: string };
  outcome: 'identical' | 'switch' | 'deviation';
  switch_to: { id: number; version_nr: string; custom: boolean } | null;
  /** Identifier a custom version would get, e.g. '1.1/acme_SN4711-1'. */
  custom_version_nr: string;
  /** Official versions of the product (predecessor choice for a new one). */
  official_versions: { id: number; version_nr: string }[];
  counts: { unchanged: number; changed: number; added: number; missing: number };
}

export type ImportTarget = ProductImportTarget | InstanceImportTarget;

/** Form fields of `POST /api/import/plan` for an upload onto a product or instance. */
export type ImportTargetRequest =
  | {
      target: 'product';
      product_type_id: number;
      mode: 'new' | 'update';
      version_nr?: string;
      predecessor_id?: number;
      version_id?: number;
    }
  | { target: 'instance'; instance_id: number };

/** What a deviating instance configuration becomes (apply request). */
export type InstanceVersionChoice =
  | { mode: 'custom' }
  | { mode: 'official'; version_nr: string; predecessor_id?: number };

/**
 * `POST /api/import/plan` — multipart/form-data, field name exactly 'file'.
 * Never set Content-Type manually when sending FormData.
 * 400 'CSV file is empty', 422 when the 'file' part is missing.
 */
export interface ImportPlanResponse {
  status: 'ok';
  filename: string | null;
  plan: ImportPlan;
}

/**
 * `POST /api/import/apply`. ALWAYS transmit the full decision map for every
 * non-'unchanged' entry: a missing entry_id is treated as 'incoming', so a
 * partial map applies what the user meant to keep.
 * 404 => the 30-minute plan TTL expired, the CSV must be re-uploaded.
 */
export interface ImportApplyRequest {
  plan_id: string;
  decisions: Record<string, ImportDecision>;
  /** Required when an instance upload deviates from every existing version. */
  version_choice?: InstanceVersionChoice;
}

export interface ImportApplyResult {
  plan_id: string;
  /** entry_ids */
  created: string[];
  /** entry_ids */
  updated: string[];
  /** entry_ids of Stücklisteneinträge removed on request */
  removed?: string[];
  /** entry_ids decided 'existing' */
  skipped: string[];
  /** '{entry_id}: {message}' (German) */
  errors: string[];
  /** Instance uploads: what happened to the instance's current version. */
  instance?: {
    action: 'unchanged' | 'switched' | 'created' | 'failed';
    version_id?: number;
    version_nr?: string;
    custom?: boolean;
  };
}

export interface ImportApplyResponse {
  status: ApiStatus;
  result: ImportApplyResult;
}

/** Client-side state of the merge review (not a wire type). */
export interface ImportReviewState {
  filename: string | null;
  plan: ImportPlan | null;
  /** entry_id -> decision, for non-'unchanged' entries only. */
  decisions: Record<string, ImportDecision>;
  showUnchanged: boolean;
}

/* ------------------------------------------------------------------------- *
 * 9. System / operations page — scans, schema, assets, settings
 * ------------------------------------------------------------------------- */

/** `GET /health` — public, must never require a session (Docker HEALTHCHECK). */
export interface HealthResponse {
  status: 'ok';
}

/** `GET /settings` — echo of the effective config. MUST stay secret-free. */
export interface SettingsResponse {
  app_version: string;
  app_revision: string;
  app_variant: 'standard' | 'simple' | 'demo';
  demo_reset_enabled: boolean;
  data_dir: string;
  sbom_dir: string;
  log_dir: string;
  dt_url: string;
  dt_frontend_url: string;
  dt_api_key_configured: boolean;
  dt_verify_ssl: boolean;
  dt_project_version: string;
  dt_settings_source: 'environment' | 'runtime';
  cmdbuild_url: string;
  cmdbuild_api: string;
  scheduler_enabled: boolean;
  /** e.g. '02:30 Europe/Vienna' */
  scheduler_cron: string;
  /** Native DT KEV projection schedule, always UTC. */
  kev_reconcile_cron: string;
}

/** API key is write-only: omit it to retain the configured value. */
export interface DependencyTrackSettingsUpdate {
  dt_url: string;
  dt_frontend_url?: string;
  dt_api_key?: string;
  clear_api_key: boolean;
  dt_verify_ssl: boolean;
  dt_project_version: string;
}

export interface DependencyTrackSettingsUpdateResponse {
  status: 'ok';
  settings: SettingsResponse;
  /** True only when the server authenticated a non-mutating DT portfolio read. */
  connection_validated: boolean;
  /** A different DT URL or project version requires project reassociation. */
  reexport_required: boolean;
}

export interface AssetSummary {
  asset_id: string | null;
  klass: string | null;
  component_count: number | null;
  sbom_path: string | null;
  last_upload_at: IsoDateTime | null;
  last_upload_project: string | null;
  last_upload_error: string | null;
}

export interface AssetsResponse {
  count: number;
  items: AssetSummary[];
}

/**
 * `GET /assets/{asset_id}` returns the raw stored record (shape owned by
 * app/services/asset_store.py, free-form) — read the known keys, keep the rest.
 */
export interface AssetRecord {
  asset_id?: string | null;
  klass?: string | null;
  component_count?: number | null;
  sbom_path?: string | null;
  last_upload_at?: IsoDateTime | null;
  last_upload_project?: string | null;
  last_upload_error?: string | null;
  uploads?: DTUploadResult[];
  [key: string]: unknown;
}

/** pydantic model with `extra='allow'` — hence the index signature. */
export interface DTUploadResult {
  upload_id: string;
  started_at: IsoDateTime;
  finished_at: IsoDateTime;
  /** 'webhook' | 'scheduler' | 'manual' | … */
  source: string;
  klass: string;
  asset_id: string | null;
  sbom_path: string;
  project_name: string;
  project_version: string;
  project_uuid: string | null;
  token: string | null;
  error: string | null;
  [key: string]: unknown;
}

/** `POST /rescan/{asset_id}` (ScanResponse). 404 on an unknown asset id. */
export interface RescanResponse {
  upload_id: string | null;
  /** 'ok' | 'error' */
  status: string;
  message: string;
  result: DTUploadResult | null;
  results: DTUploadResult[] | null;
  extra: Record<string, unknown> | null;
}

/* ------------------------------------------------------------------------- *
 * CSAF advisory sources — /api/csaf/*
 *
 * Mirrors app/services/csaf_sources.py (the catalogue) and
 * app/services/csaf_service.py (the sync result). The sources are operator
 * data, not configuration: which vendor feeds are pulled is edited in the
 * system console while the service runs.
 * ------------------------------------------------------------------------- */

/**
 * How a source discovers its advisory documents.
 *   - `rolie`    the JSON feed CSAF providers publish — the preferred kind,
 *   - `feed`     an RSS 2.0 / Atom feed (the backend derives the JSON URL),
 *   - `document` one CSAF JSON URL, re-fetched every run,
 *   - `manual`   uploads only, no network,
 *   - `euvd`     ENISA EUVD queried once per portfolio vendor.
 */
export type CsafSourceKind = 'rolie' | 'feed' | 'document' | 'manual' | 'euvd';

/** Labels, mirrored from `KIND_LABELS` in app/services/csaf_sources.py. */
export const CSAF_SOURCE_KIND_LABELS: Readonly<Record<CsafSourceKind, string>> = translatedRecord<CsafSourceKind>({
  rolie: msg`ROLIE-Feed (CSAF-JSON)`,
  feed: msg`RSS-/Atom-Feed`,
  document: msg`Einzelnes CSAF-Dokument`,
  manual: msg`Manuelle Uploads`,
  euvd: msg`ENISA EUVD (EU-Datenbank)`,
});

/** How the source's advisories are represented in Dependency-Track. */
export type CsafRecordStyle = 'cve';

/** Labels, mirrored from `RECORD_STYLE_LABELS` in csaf_sources.py. */
export const CSAF_RECORD_STYLE_LABELS: Readonly<Record<CsafRecordStyle, string>> = translatedRecord<CsafRecordStyle>({
  cve: msg`Ein Eintrag pro CVE (CVE-2025-24811)`,
});

/** Outcome of a source's last sync run — `null` until it has run once. */
export type CsafSourceStatus = 'ok' | 'partial' | 'error';

export interface CsafSource {
  id: string;
  name: string;
  kind: CsafSourceKind;
  /** Server-rendered label; prefer it over the local map for unknown kinds. */
  kind_label: string;
  /** Empty for `manual` sources. */
  url: string;
  enabled: boolean;
  /** Compatibility field: always true, all CVEs are imported. */
  import_all: boolean;
  /** Always one record per CVE. */
  record_style: CsafRecordStyle;
  /** Server-rendered label for `record_style`. */
  record_style_label: string;
  created_at: string;
  last_sync_at: string | null;
  last_status: CsafSourceStatus | null;
  last_message: string | null;
  /** Distinct CVEs checked in the last run; null until a run records CVE counts. */
  last_cves: number | null;
  /** Distinct checked CVEs affecting the portfolio, independent of import settings. */
  last_matched_cves: number | null;
  /** Legacy document counts, retained for API compatibility. */
  last_documents: number;
  last_matched: number;
  last_created: number;
  last_updated: number;
}

/** `GET /api/csaf/sources`. `kinds` is the server's vocabulary — use it to
 * build the picker so a kind added server-side needs no frontend release. */
export interface CsafSourcesResponse {
  monitoring: { enabled: boolean; interval_minutes: number; next_run_at: string | null };
  sources: CsafSource[];
  kinds: { kind: CsafSourceKind; label: string }[];
  record_styles: { style: CsafRecordStyle; label: string }[];
  presets: CsafSourcePreset[];
}

/** Currently stored DT CVEs attributed to one CSAF source. */
export interface CsafSourceVulnerability {
  uuid: string;
  cve_id: string;
  title: string | null;
  severity: string;
  published: string | number | null;
  dt_url: string | null;
}

/** `GET /api/csaf/sources/{id}/vulnerabilities`, paginated in DT. */
export interface CsafSourceVulnerabilitiesResponse {
  source_id: string;
  total: number;
  page: number;
  page_size: number;
  items: CsafSourceVulnerability[];
  dt_url: string | null;
}

/** Curated official source template returned by the server. */
export interface CsafSourcePreset {
  id: string;
  name: string;
  kind: CsafSourceKind;
  url: string;
  description: string;
  official_url: string;
}

/** `POST /api/csaf/sources`. `url` must be empty for kind `manual`. */
export interface CreateCsafSourceRequest {
  name: string;
  kind: CsafSourceKind;
  url: string;
  enabled?: boolean;
  import_all?: boolean;
  record_style?: CsafRecordStyle;
}

/** `POST /api/csaf/sources/test` — a write-free probe of an unsaved source. */
export interface CsafSourceTestRequest {
  kind: CsafSourceKind;
  url: string;
}

export interface CsafSourceTestSample {
  id: string;
  csaf_version: string | null;
  /** Distinct CVEs in the sampled document, not the whole feed. */
  cves: number | null;
}

/**
 * The probe only fetches and validates a small sample. It never adds the source,
 * updates its last-run state or writes findings to Dependency-Track.
 */
export interface CsafSourceTestResponse {
  status: 'ok' | 'warning';
  kind: CsafSourceKind;
  reachable: boolean;
  compatible: boolean;
  documents: number;
  sample: CsafSourceTestSample | null;
  message: string;
  warnings: string[];
}

/** `PATCH /api/csaf/sources/{id}` — send only what changed; kind is immutable. */
export interface UpdateCsafSourceRequest {
  name?: string;
  url?: string;
  enabled?: boolean;
  import_all?: boolean;
  record_style?: CsafRecordStyle;
}

export interface CsafSourceResponse {
  status: ApiStatus;
  source: CsafSource;
}

/** Per-source breakdown of one sync run. */
export interface CsafSyncSourceResult {
  id: string;
  name: string;
  kind: CsafSourceKind;
  status: CsafSourceStatus;
  cves_total: number;
  cves_matched: number;
  documents: number;
  matched: number;
  errors: number;
}

/** `POST /scan/csaf` and `POST /api/csaf/sync` (CSAF → DT internal vulns). */
export interface CsafSyncResult {
  sources_synced: string[];
  /** Distinct CVEs across all checked sources. */
  cves_total: number;
  /** Distinct CVEs affecting the portfolio. */
  cves_matched: string[];
  advisories_total: number;
  advisories_fetched: number;
  advisories_matched: string[];
  created: string[];
  updated: string[];
  unchanged: number;
  skipped_nvd_enriched: string[];
  skipped_blocked: number;
  /** Feed entries without a reachable CSAF JSON — an RSS feed linking HTML only. */
  skipped_no_document: string[];
  reanalyzed_projects: number;
  errors: string[];
  per_source: CsafSyncSourceResult[];
}

export interface CsafSyncResponse {
  status: ApiStatus;
  result: CsafSyncResult;
}

/** `POST /api/csaf/sources/{id}/documents` — a sync result for one upload. */
export interface CsafUploadResponse extends CsafSyncResponse {
  filename: string | null;
}

/** `POST /schema/assetradar`. */
export interface SchemaProvisionResult {
  created_classes: string[];
  skipped_classes: string[];
  created_attributes: string[];
  skipped_attributes: string[];
  created_domains: string[];
  skipped_domains: string[];
  created_lookups: string[];
  skipped_lookups: string[];
  /** Seeded reference cards, e.g. `Country/DE` (DEV-49). */
  created_cards: string[];
  skipped_cards: string[];
  /** Classes added to the CMDBuild navigation menu, e.g. `AssetRadar/Auslieferung/Länder` (DEV-135). */
  created_menu_entries: string[];
  errors: string[];
}

export interface SchemaProvisionResponse {
  status: ApiStatus;
  result: SchemaProvisionResult;
}

/**
 * `POST /schema/assetradar/upload` — provision an uploaded schema file.
 *
 * Additive by construction: the run only ever creates what CMDBuild is
 * missing, so `result.created_*` are the new objects and `result.skipped_*`
 * the ones that already existed. With `dry_run` nothing was written and
 * `created_*` say what a real run WOULD create.
 */
export interface SchemaUploadResponse {
  status: ApiStatus;
  filename: string | null;
  dry_run: boolean;
  /** What the file itself declares — independent of what CMDBuild already has. */
  counts: {
    classes: number;
    attributes: number;
    relations: number;
    lookups: number;
    lookup_values: number;
  };
  /** Non-fatal remarks from the parser (unknown keys, extended built-in class, …). */
  warnings: string[];
  result: SchemaProvisionResult & { dry_run: boolean };
}

/** `GET /schema/assetradar/export` — the current model as an editable YAML file. */
export interface SchemaExportResponse {
  status: ApiStatus;
  filename: string;
  yaml: string;
  counts: {
    classes: number;
    attributes: number;
    relations: number;
    lookups: number;
    lookup_values: number;
  };
}

/** `POST /schema/assetradar/wipe` — DESTRUCTIVE, needs an explicit confirmation. */
export interface SchemaWipeResult {
  /** CMDBuild class name -> number of cards deleted. */
  deleted: Record<string, number>;
  errors: string[];
}

export interface SchemaWipeResponse {
  status: ApiStatus;
  result: SchemaWipeResult;
}

/** `GET /api/demo/backups` — one frozen demo state. */
export interface DemoBackup {
  id: string;
  /** Free text, may be empty. */
  label: string;
  created_at: IsoDateTime;
  database: string;
  server_version: string;
  assets: number;
  sboms: number;
  dump_bytes: number;
  size_bytes: number;
  duration_seconds: number;
}

/** Result of a completed restore job. */
export interface DemoRestoreResult {
  backup: DemoBackup;
  restored_at: IsoDateTime;
  duration_seconds: number;
  timings_seconds: Record<string, number>;
  preserved: string[];
}

/** Background backup or restore; progress is reported by the server. */
export interface DemoJob {
  id: string;
  kind: 'backup' | 'restore';
  /** The backup label (restore: of the backup being restored). */
  label?: string;
  backup_id?: string;
  status: 'running' | 'completed' | 'failed';
  phase: string;
  message: string;
  progress: number;
  started_at: IsoDateTime;
  finished_at: IsoDateTime | null;
  result: DemoBackup | DemoRestoreResult | null;
  error: string | null;
}

/** `POST /schema/assetradar/cleanup-deprecated` — destructive schema surgery. */
export interface SchemaCleanupResult {
  /** 'cards:Klass:12' | 'attr:Klass/Attr' | 'domain:X' | 'class:X' */
  removed: string[];
  errors: string[];
}

export interface SchemaCleanupResponse {
  status: ApiStatus;
  result: SchemaCleanupResult;
}

/** One SBOM group of `POST /export/produktversionen` (one DT project per version). */
export interface ExportGroup {
  /** 'Produkt@VersionNr' — DT project name = product, project version = VersionNr. */
  produktversion: string;
  produkt: string;
  version: string;
  /** Instances currently running this version. */
  instances: number;
  asset_id: string;
  component_count: number;
  sbom_path: string;
  upload: DTUploadResult;
}

export interface ExportProduktversionenResult {
  event: 'produktversion_export';
  action: 'uploaded';
  versions: number;
  component_count: number;
  /** 'Produkt@VersionNr' of versions with an empty Stückliste. */
  skipped_no_config: string[];
  groups: ExportGroup[];
  uploads: DTUploadResult[];
  upload: DTUploadResult | null;
}

export interface ExportProduktversionenResponse {
  status: 'ok';
  result: ExportProduktversionenResult;
}

/** One affected component of `GET /cve/{cve_id}/exploitability`. */
export interface ExploitabilityComponent {
  project_name: string | null;
  project_version: string | null;
  project_uuid: string | null;
  component_name: string | null;
  component_version: string | null;
  purl: string | null;
}

export interface ExploitabilityFacts {
  known_exploited: boolean;
  /** Preferred assertion publication date, 'YYYY-MM-DD'. */
  kev_date_added: string | null;
  /** Always null with DT 5.1's public assertion API. */
  kev_due_date: string | null;
  kev_known_ransomware: 'Known' | 'Not known' | null;
  kev_sources: Array<string | null>;
  epss_score: number | null;
  epss_percentile: number | null;
  epss_threshold: number;
  cvss_base_score: number | null;
  severity: string | null;
}

/**
 * The ENISA EUVD (EU Vulnerability Database) view of the same CVE — additive
 * enrichment on `GET /cve/{id}/exploitability`, `null` when EUVD has no record
 * or is unreachable (it is an external service with visible availability gaps).
 *
 * Why it is worth showing: EUVD scores most records with CVSS **4.0** where NVD
 * still carries 3.1 (CVE-2024-50310: NVD 7.5 / EUVD 8.7), and its `EUVD-…` id is
 * the identifier CRA reporting to ENISA speaks in.
 */
export interface EuvdFacts {
  /** e.g. `EUVD-2024-44808`. */
  id: string;
  /** Public detail page. */
  url: string | null;
  /** `"4.0"`, `"3.1"`, … — which CVSS version `cvss_base_score` belongs to. */
  cvss_version: string | null;
  cvss_base_score: number | null;
  cvss_vector: string | null;
  /** PROBABILITY 0…1 — the backend rescales EUVD's percentage. */
  epss_score: number | null;
  assigner: string | null;
  date_published: IsoDateTime | null;
  date_updated: IsoDateTime | null;
  /** GHSA / PYSEC / … ids, excluding the CVE that was asked for. */
  aliases: string[];
  /** On the EU "exploited" list. Reported only — it does NOT set `exploitable`. */
  known_exploited_eu: boolean;
}

/**
 * `GET /cve/{cve_id}/exploitability?source=`.
 * 503 when Dependency-Track is not configured, 502 on a DT error.
 */
export interface ExploitabilityResponse {
  cve: string;
  /** KEV hit OR epss_score >= epss_threshold. EUVD does not influence it. */
  exploitable: boolean;
  exploitability: ExploitabilityFacts;
  euvd: EuvdFacts | null;
  in_dependency_track: boolean;
  affected_component_count: number;
  affected_components: ExploitabilityComponent[];
}

/* ------------------------------------------------------------------------- *
 * 9a. Stammdaten — /api/cmdb/*  (mirror of app/services/cmdb_crud.py)
 * ------------------------------------------------------------------------- */

/**
 * CMDBuild attribute types the generic form renders. The server sends the raw
 * schema type, so anything not listed here must still render (as a text input) —
 * hence the `(string & {})` escape hatch.
 */
export type CmdbAttrType =
  | 'string'
  | 'text'
  | 'boolean'
  | 'date'
  | 'dateTime'
  | 'double'
  | 'integer'
  | 'reference'
  | 'lookup'
  | (string & {});

/** One editable field. `system: true` fields are displayed but never submitted. */
export interface CmdbAttribute {
  name: string;
  /** German label. */
  label: string;
  type: CmdbAttrType;
  /** The English schema description — shown as the field hint. */
  help: string;
  required: boolean;
  /**
   * Written by a pipeline (KEV scan, SBOM export, fan-out). Rendered read-only:
   * a hand-edit would survive only until that job runs again.
   */
  system: boolean;
  /** Set for `type: 'reference'` — the class whose cards the picker offers. */
  target_class: string | null;
  /** Set for `type: 'lookup'` — the key into `CmdbMetaResponse.lookups`. */
  lookup_type: string | null;
}

/** An inbound reference, i.e. a reason a delete is refused. */
export interface CmdbReferencedBy {
  class: string;
  attribute: string;
  label: string;
}

export interface CmdbClassMeta {
  name: string;
  label: string;
  label_plural: string;
  /** False for the machine-generated classes (VulnerabilityEvent, Benachrichtigung). */
  can_create: boolean;
  search_attrs: string[];
  referenced_by: CmdbReferencedBy[];
  attributes: CmdbAttribute[];
}

export interface CmdbLookupValue {
  id: number;
  /** The stable code — what create/update submits. */
  code: string;
  /** German description shown in the select. */
  label: string;
}

export interface CmdbMetaResponse {
  classes: CmdbClassMeta[];
  /** Keyed by lookup type name; empty array when the type is not provisioned. */
  lookups: Record<string, CmdbLookupValue[]>;
}

/** `-1` means the class could not be read (schema not provisioned). */
export interface CmdbCountsResponse {
  counts: Record<string, number>;
}

export interface CmdbOption {
  id: number;
  label: string;
  /** ProduktVersion options only: the product the version belongs to. */
  product_id?: number | null;
}

export interface CmdbOptionsResponse {
  options: Record<string, CmdbOption[]>;
}

/* Stückliste per product version (DEV-114) */

/** Triage verdict of one CVE on a component version. */
export interface BomAssessment {
  cve_id: string;
  /** OFFEN, BETROFFEN, NICHT_BETROFFEN or BEHOBEN. */
  verdict: string;
  event_id: number | null;
}

export interface BomComponent {
  entry_id: number | null;
  component_version_id: number;
  component_type_id: number | null;
  name: string;
  manufacturer: string;
  version: string;
  /** CPE part: a (application), o (operating system), h (hardware). */
  part: string;
  kev_affected: boolean;
  kev_cves: string[];
  assessments: BomAssessment[];
}

export interface BomVersion {
  version_id: number;
  instance_count: number;
  components: BomComponent[];
}

export interface BomOverviewResponse {
  versions: BomVersion[];
}

export interface BomSaveResponse {
  version_id: number;
  added: number[];
  removed: number[];
  errors: { component_version_id: number; action: 'add' | 'remove'; message: string }[];
}

/* ------------------------------------------------------------------------- *
 * Beziehungen (asset relationship graph)
 * ------------------------------------------------------------------------- */

/**
 * One card as a graph node. `id` is `"<Klasse>:<cardId>"` — unique across
 * classes, which card ids alone are not. `state` is the class's own risk
 * verdict ('' when the class has none) and `degree` the number of edges the
 * server counted, which the client uses to size the node.
 */
export interface GraphNode {
  id: string;
  klass: string;
  cardId: number;
  label: string;
  detail: string;
  /** Owner of the record (e.g. the Kunde of a Produktinstanz), for pickers. */
  context?: string;
  state: '' | 'ok' | 'warn' | 'danger';
  degree: number;
}

/** One reference between two cards. `kind` is the source attribute, or the
 *  join class it was collapsed from. */
export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  kind: string;
}

export interface GraphClass {
  name: string;
  label: string;
  plural: string;
  count: number;
}

export interface GraphResponse {
  generated_at: string;
  /** Join classes rendered as edges instead of nodes. */
  collapsed: string[];
  /** Classes CMDBuild could not deliver (schema not provisioned). */
  unavailable: string[];
  classes: GraphClass[];
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/**
 * One card, projected twice: `values` is form-ready (references as card ids,
 * lookups as codes, booleans as booleans) and `display` is table-ready (every
 * value already resolved to a German string).
 */
export interface CmdbCardRow {
  id: number;
  label: string;
  code: string | null;
  values: Record<string, unknown>;
  display: Record<string, string>;
  /** Only present on the single-card read. */
  referenced_by?: CmdbCardBlocker[];
}

export interface CmdbCardBlocker {
  class: string;
  class_label: string;
  attribute: string;
  attribute_label: string;
  id: number;
  label: string;
}

export interface CmdbListResponse {
  class: string;
  count: number;
  rows: CmdbCardRow[];
}

export interface CmdbDeleteResponse {
  deleted: number;
  class: string;
}

/* ------------------------------------------------------------------------- *
 * 9b. Dependency-Track mirror — /api/dt/*
 * ------------------------------------------------------------------------- */

/** DT severity vocabulary, worst first. */
export type DtSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO' | 'UNASSIGNED';

export const DT_SEVERITY_ORDER = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'INFO',
  'UNASSIGNED',
] as const satisfies readonly DtSeverity[];

/**
 * Why a project has no findings — the ONLY field to branch on.
 *
 *   'kein-projekt' no SBOM exported yet
 *   'unbekannt'    DT unreachable / project gone → NOT the same as "clean"
 *   'ausstehend'   SBOM uploaded, DT still computing its metrics
 *   'ok'           metrics available
 */
export type DtScanState = 'kein-projekt' | 'unbekannt' | 'ausstehend' | 'ok';

/** The instance identity every DT payload is keyed by. */
export interface DtInstanceHead {
  instanz_id: number;
  label: string;
  seriennummer: string;
  kunde: string;
  produkt: string;
  version: string;
  project_uuid: string | null;
}

export interface DtProjectState {
  scan_state: DtScanState;
  project_name: string | null;
  last_bom_import: CmdbDateTime;
  last_metrics: CmdbDateTime;
  risk_score: number | null;
  component_count: number | null;
  findings_total: number;
  /** Partial: absent keys are zero, an EMPTY object means "not scanned". */
  severities: Partial<Record<DtSeverity, number>>;
  /** German explanation when `scan_state !== 'ok'`. */
  note: string | null;
}

export type DtInstanceRow = DtInstanceHead & DtProjectState;

export interface DtInstancesResponse {
  dt_configured: boolean;
  dt_url: string;
  count: number;
  rows: DtInstanceRow[];
}

export interface DtFinding {
  source_links?: { label: string; url: string }[];
  cve_id: string;
  source: string | null;
  /** Configured CSAF catalogue name; raw DT source remains its technical enum. */
  source_label: string | null;
  severity: DtSeverity;
  cvss_score: number | null;
  epss_score: number | null;
  epss_percentile: number | null;
  /** Native Dependency-Track KEV verdict (all enabled sources). */
  kev: boolean;
  title: string;
  description: string;
  published: CmdbDateTime;
  component: string;
  component_version: string;
  purl: string | null;
  cpe: string | null;
  /** DT's own analysis state. Read-only here — triage lives in AssetRadar. */
  dt_analysis_state: string | null;
  suppressed: boolean;
}

export type DtFindingsResponse = DtInstanceHead &
  DtProjectState & {
    findings: DtFinding[];
    include_suppressed: boolean;
    kev_count?: number;
  };

/** One product (ProduktType + Version) hit by a CVE. */
export interface DtCveProdukt {
  produkt: string;
  version: string;
  instanz_count: number;
  kunden: string[];
}

/** One delivered instance hit by a CVE, with the components that carry it. */
export interface DtCveInstanz extends DtInstanceHead {
  komponenten: {
    name: string;
    version: string;
    purl: string | null;
    severity: DtSeverity;
    suppressed: boolean;
    dt_analysis_state: string | null;
  }[];
}

/**
 * One CVE across the whole fleet. Severity/CVSS/EPSS are the WORST value seen
 * on any affected instance, so a CVE never reads milder than its worst hit.
 */
export interface DtCveRow {
  source_links?: { label: string; url: string }[];
  cve_id: string;
  source: string | null;
  /** Configured CSAF catalogue name; raw DT source remains its technical enum. */
  source_label: string | null;
  severity: DtSeverity;
  cvss_score: number | null;
  epss_score: number | null;
  epss_percentile: number | null;
  kev: boolean;
  title: string;
  description: string;
  published: CmdbDateTime;
  produkte: DtCveProdukt[];
  produkt_count: number;
  instanzen: DtCveInstanz[];
  instanz_count: number;
  /** "name version" per affected component, deduplicated across instances. */
  komponenten: string[];
}

export interface DtCvesResponse {
  dt_configured: boolean;
  dt_url: string;
  include_suppressed: boolean;
  count: number;
  kev_count: number;
  rows: DtCveRow[];
  /**
   * Instances that could not contribute — no SBOM or DT unreachable. NOT the
   * same as "clean": a CVE may well affect them unseen.
   */
  nicht_geprueft: (DtInstanceHead & { scan_state: DtScanState; note: string | null })[];
}

export interface DtComponent {
  name: string;
  version: string;
  group: string | null;
  publisher: string | null;
  purl: string | null;
  cpe: string | null;
  findings: number;
  worst_severity: DtSeverity | null;
}

/** A KomponenteVersion AssetRadar believes runs on the instance. */
export interface DtKatalogVersion {
  id: number;
  label: string;
  bezeichnung: string;
  version: string;
  vendor: string;
  product: string;
  kev_betroffen: boolean;
  kev_cves: string[];
  /** True when the instance runs a custom version whose official base lacks this component. */
  abweichung: boolean;
}

export type DtSbomResponse = DtInstanceHead &
  DtProjectState & {
    components: DtComponent[];
    /** What the CMDB says should be there — compare against `components`. */
    katalog_versionen: DtKatalogVersion[];
  };

/* ------------------------------------------------------------------------- *
 * 9c. CRA-Check — /api/cra-check/*
 * ------------------------------------------------------------------------- */

/**
 * `unbekannt` is NOT `ok`: a check that could not run has not passed. It ranks
 * between `fehler` and `warnung` — see app/services/cra_check_service.py.
 */
export type CraState = 'ok' | 'warnung' | 'fehler' | 'unbekannt';

export const CRA_STATE_LABELS: Readonly<Record<CraState, string>> = translatedRecord<CraState>({
  ok: msg`Konform`,
  warnung: msg`Warnung`,
  fehler: msg`Fehler`,
  unbekannt: msg`Unbekannt`,
});

export const CRA_STATE_TONE: Readonly<Record<CraState, Tone>> = {
  ok: 'good',
  warnung: 'warn',
  fehler: 'alert',
  unbekannt: 'neutral',
};

/** Which half of the CRA obligation a check belongs to. */
export type CraGroup = 'reife' | 'fristen';

export interface CraCheck {
  key: string;
  label: string;
  state: CraState;
  /** What was measured, German. */
  detail: string;
  /** What to do about it — empty when there is nothing to do. */
  hint: string;
  group: CraGroup;
}

/** An unreported clock of a vulnerability case (DEV-132). */
export interface CraDeadline {
  case_id: number;
  titel: string;
  cves: string[];
  clock: 'frist24h' | 'frist72h' | 'frist14t';
  clock_label: string;
  due: IsoDateTime;
  /** Negative once overdue. */
  remaining_hours: number;
  overdue: boolean;
}

export interface CraCheckRow {
  instanz_id: number;
  label: string;
  seriennummer: string;
  kunde: string;
  kunde_id: number | null;
  produkt: string;
  produktversion: string;
  project_uuid: string | null;
  /** The worst of `checks` — what the traffic light shows. */
  state: CraState;
  state_reife: CraState;
  state_fristen: CraState;
  checks: CraCheck[];
  deadlines: CraDeadline[];
  komponenten_count: number;
  kev_cves: string[];
  betroffen_cves: string[];
  severities: Partial<Record<DtSeverity, number>>;
  findings_total: number;
}

export interface CraCheckSummary {
  total: number;
  by_state: Record<CraState, number>;
  compliant: number;
  fristen_ueberfaellig: number;
  fristen_bald: number;
  /** check key → how many instances fail or warn on it. */
  failed_checks: Record<string, number>;
}

export interface CraCheckThresholds {
  sbom_max_age_days: number;
  scan_max_age_days: number;
  deadline_warn_hours: number;
}

export interface CraCheckOverviewResponse {
  generated_at: IsoDateTime;
  summary: CraCheckSummary;
  thresholds: CraCheckThresholds;
  rows: CraCheckRow[];
}

export interface CraCheckInstanceResponse {
  generated_at: IsoDateTime;
  row: CraCheckRow;
}

/* ------------------------------------------------------------------------- *
 * 9a. Verantwortlichkeit (PSIRT) — GET /api/psirt/overview (cmdb.read)
 *
 * Read model of app/services/psirt_service.py. Writes go through the generic
 * /api/cmdb/* CRUD on the classes `PsirtTeam` and `ProduktVerantwortung`.
 * ------------------------------------------------------------------------- */

/** PSIRT verdicts never report `unbekannt` — every rule reads master data only. */
export type PsirtState = Exclude<CraState, 'unbekannt'>;

export type PsirtGroup = 'organisation' | 'produkt';

/** Lookup `ProduktVerantwortung - CraKategorie`. */
export type CraKategorie = 'STANDARD' | 'WICHTIG_I' | 'WICHTIG_II' | 'KRITISCH';

export interface PsirtCheck {
  key: string;
  label: string;
  state: PsirtState;
  detail: string;
  hint: string;
  /** The CRA provision, e.g. "Art. 13 Abs. 8 · Anhang II Nr. 7". */
  reference: string;
  group: PsirtGroup;
}

export interface PsirtVerdict {
  state: PsirtState;
  erfuellt: number;
  gesamt: number;
  checks: PsirtCheck[];
}

/** Form-ready values of a `PsirtTeam` card (CRUD `values`). */
export interface PsirtTeamValues {
  Name?: string | null;
  MeldeEmail?: string | null;
  Telefon?: string | null;
  Erreichbarkeit?: string | null;
  CvdPolicyUrl?: string | null;
  SecurityTxtUrl?: string | null;
  Mitgliedstaat?: string | null;
  CsirtKoordinator?: string | null;
  MelderName?: string | null;
  MelderEmail?: string | null;
  LeitungName?: string | null;
  LeitungEmail?: string | null;
  VertretungName?: string | null;
  VertretungEmail?: string | null;
  Bemerkung?: string | null;
}

/** Form-ready values of a `ProduktVerantwortung` card. */
export interface ProduktVerantwortungValues {
  ProduktTypeID?: number | null;
  PsirtTeamID?: number | null;
  OwnerName?: string | null;
  OwnerEmail?: string | null;
  VertretungName?: string | null;
  VertretungEmail?: string | null;
  CraKategorie?: CraKategorie | null;
  SupportEnde?: string | null;
  UpdateKanal?: string | null;
  GeprueftAm?: string | null;
  Bemerkung?: string | null;
}

export interface PsirtTeam extends PsirtVerdict {
  id: number;
  label: string;
  values: PsirtTeamValues;
  /** Products assigned to this team. */
  produkte: number;
}

export interface PsirtProductRow extends PsirtVerdict {
  produkt_id: number;
  produkt: string;
  modellreihe: string;
  instanzen: number;
  kunden: number;
  /** Earliest ProduktVersion release — reference point of the 5-year rule. */
  erstes_release: string | null;
  /** `null` when no responsibility card exists yet. */
  verantwortung_id: number | null;
  values: ProduktVerantwortungValues | null;
  team_id: number | null;
  team: string;
  kategorie: CraKategorie | null;
  support_ende: string | null;
  /** Whole months until `support_ende` (negative once expired). */
  support_restmonate: number | null;
}

export interface PsirtSummary {
  produkte: number;
  vollstaendig: number;
  luecken: number;
  hinweise: number;
  ohne_zustaendigkeit: number;
  support_bald: number;
  support_abgelaufen: number;
  pruefung_faellig: number;
  teams: number;
}

export interface PsirtThresholds {
  support_min_years: number;
  support_warn_months: number;
  review_max_months: number;
}

export interface PsirtOverviewResponse {
  generated_at: IsoDateTime;
  /** False until the PSIRT classes are provisioned in CMDBuild. */
  schema_ready: boolean;
  schema_missing: string[];
  kategorien: CmdbLookupValue[];
  /** Category code → consequence for the conformity assessment. */
  kategorie_folgen: Record<string, string>;
  teams: PsirtTeam[];
  rows: PsirtProductRow[];
  summary: PsirtSummary | null;
  thresholds: PsirtThresholds;
}

/* ------------------------------------------------------------------------- *
 * 9b. Audit-Log — GET /api/audit-log (read-only, triage.read)
 * ------------------------------------------------------------------------- */

/** What one timeline entry records. */
export type AuditKind =
  | 'erkannt'
  | 'bewertung'
  /** Component set to „In Prüfung“. */
  | 'pruefung'
  /** Verdict of a product version above the component (DEV-100). */
  | 'produktbewertung'
  /** Verdict of an instance running such a version (DEV-100). */
  | 'instanzbewertung'
  | 'wiedereroeffnet'
  | 'behoben'
  | 'meldung'
  /** A mail queue step (DEV-93): queued, withdrawn, sending begun, released, failed. */
  | 'postausgang'
  | 'versand'
  | 'advisory'
  | 'nachweis'
  /** Vulnerability case (DEV-132): opened, obsolete, completed, reopened. */
  | 'fall'
  | 'kernangaben'
  /** A report to the authority recorded or taken back, measured against its clock. */
  | 'behoerdenmeldung'
  | 'abhilfe'
  /** STARTED / DUE_SOON / OVERDUE raised by the deadline check. */
  | 'fristereignis';

/** Component verdicts use the German lookup, product/instance verdicts the shared vocabulary. */
/** Steps of the Security Advisory in the audit log (DEV-129). */
export type AdvisoryAuditStep = 'SAVED' | 'RELEASED' | 'WITHDRAWN';

export type DeadlineEventType = 'STARTED' | 'DUE_SOON' | 'OVERDUE';

export type AuditVerdict = Bewertung | AssessmentStatus | AdvisoryAuditStep | DeadlineEventType;

export interface AuditKomponente {
  name: string | null;
  version: string | null;
  hersteller: string | null;
}

/** Context every entry and case file share (the VulnerabilityEvent). */
export interface AuditEventContext {
  event_id: number;
  cve_id: string | null;
  base_score: number | string | null;
  aktiv_ausgenutzt: boolean;
  komponente: AuditKomponente;
}

/** One reconstructed step. Timestamps are normalised ISO (UTC, 'Z'). */
export interface AuditEntry extends AuditEventContext {
  id: string;
  ts: IsoDateTime | null;
  kind: AuditKind;
  actor: string | null;
  actor_type: 'person' | 'system';
  /** Verdict after this step (verdict kinds only). */
  bewertung: AuditVerdict | null;
  /** Verdict before this step — a re-assessment when it was already decided. */
  vorher: AuditVerdict | null;
  begruendung: string | null;
  kunden: string[];
  detail: string | null;
  /** The CRA deadline of the step (case kinds only; customer mails have none). */
  frist: IsoDateTime | null;
  /** Reported before `frist` (behoerdenmeldung) — null when unknown. */
  fristgerecht: boolean | null;
}

/** One case file per VulnerabilityEvent. */
export interface AuditCase extends AuditEventContext {
  bewertung: Bewertung;
  begruendung: string | null;
  bewertet_von: string | null;
  bewertet_am: IsoDateTime | null;
  erstellt_am: IsoDateTime | null;
  betroffene_kunden: number;
  entscheidungen: number;
  neubewertungen: number;
  /** Hours from detection to the first human verdict. */
  reaktionszeit_h: number | null;
  /** Customer mails. */
  meldungen: { gesamt: number; versendet: number };
  /** Clocks of the vulnerability cases above this hit (DEV-132). */
  fristen: AuditClockFigures;
  /** Product versions containing the component and how many have a verdict. */
  produkte?: { gesamt: number; bewertet: number; erneut_pruefen: number };
  dokumente: { advisory: boolean; attachments: number };
  letzte_aktivitaet: IsoDateTime | null;
  letzter_bewerter: string | null;
}

export interface AuditClockFigures {
  gemeldet: number;
  fristgerecht: number;
  ueberfaellig: number;
}

export interface AuditActor {
  name: string;
  entscheidungen: number;
  aktivitaeten: number;
  letzte: IsoDateTime | null;
}

export interface AuditKennzahlen {
  faelle: number;
  entscheidungen: number;
  produktbewertungen?: number;
  instanzbewertungen?: number;
  begruendet: number;
  neubewertungen: number;
  automatisch: number;
  reaktionszeit_median_h: number | null;
  bewertungslage: Record<Bewertung, number>;
  meldungen: { gesamt: number; versendet: number };
  /** Every vulnerability case counted once. */
  fristen: AuditClockFigures;
  nachweise: number;
  advisories: number;
  bewerter: AuditActor[];
}

export interface AuditLogResponse {
  generated_at: IsoDateTime;
  /** False when the evidence store could not be read — upload steps are then missing. */
  dokumente_verfuegbar: boolean;
  kennzahlen: AuditKennzahlen;
  /** Newest first. */
  eintraege: AuditEntry[];
  /** Most recently active first. */
  faelle: AuditCase[];
}

/* ------------------------------------------------------------------------- *
 * 10. Client-side view models (not wire types)
 * ------------------------------------------------------------------------- */

/** The dashboard needs BOTH payloads in one state: the notify gate joins them. */
export interface DashboardData {
  overview: DashboardOverviewResponse;
  /** Fails soft — see VulnerabilityEventsResponse. */
  events: VulnerabilityEventsResponse;
  /** The CVE view (DEV-133); null without triage.read. */
  cves: CveDashboardResponse | null;
}

/* --- Dashboard by vulnerability (DEV-133) ------------------------------- */

/** Where a product stands with a CVE; a product can stand in two areas. */
export type CveArea = 'assess' | 'in_progress' | 'done' | 'none';

/** One customer's mail of the case: what needs attention first. */
export type CustomerMailState = 'open' | 'queued' | 'failed' | 'sent';

export interface CveDashboardVersion {
  version_id: number;
  version_nr: string | null;
  custom: boolean;
  status: AssessmentStatus;
  proposal: AssessmentStatus;
  confirmed: boolean;
  needs_review: boolean;
  instance_count: number;
  /** The triage row to open for this version. */
  event_id: number | null;
}

export interface CveDashboardCustomer {
  customer_id: number | null;
  name: string | null;
  country_iso: string | null;
  is_eu: boolean;
  notification: CustomerMailState | null;
  instances: Array<{
    instance_id: number;
    serial: string | null;
    version_nr: string | null;
    status: AssessmentStatus;
    confirmed: boolean;
    deviates: boolean;
  }>;
}

export interface CveDashboardProduct {
  product_type_id: number | null;
  product_name: string;
  case: {
    id: number;
    status: CaseStatus;
    contradiction: boolean;
    clocks: CaseClock[];
    next_deadline: IsoDateTime | null;
    /** Missing inputs of the next report to be made. */
    missing: string[];
    advisory_status: AdvisoryStatus | null;
  } | null;
  mails: { total: number; sent: number; open: number; queued: number; failed: number };
  /** What keeps the case from being done, in plain words. */
  open_items: string[];
  areas: CveArea[];
  /** An unreported deadline has passed. */
  overdue: boolean;
  versions: CveDashboardVersion[];
  customers: CveDashboardCustomer[];
}

export interface CveDashboardEntry {
  cve: string;
  description: string | null;
  base_score: number | string | null;
  actively_exploited: boolean;
  /** The oldest KEV hit of the CVE. */
  first_hit_at: IsoDateTime | null;
  products: CveDashboardProduct[];
}

/** `GET /api/dashboard/cves` (triage.read). */
export interface CveDashboardResponse {
  generated_at: IsoDateTime;
  cves: CveDashboardEntry[];
  summary: { cves: number; due_deadlines: number; overdue_deadlines: number; open_mails: number };
}

/** Client-side dashboard filters. Only `onlyAffected` hits the server. */
export interface DashboardFilters {
  /** Server-side query param, default true. */
  onlyAffected: boolean;
  /** Client-side substring filter over Kunde/Produkt/S-N/Komponente/CVE. */
  search: string;
  /** Client-side: affected_count > 0 && !notified. Default false. */
  onlyUnnotified: boolean;
  /** Client-side filter on bewertung === 'OFFEN'. Default true. */
  onlyOpenEvents: boolean;
}

/** Navigation entry rendered by Layout — `permission: null` = any logged-in user. */
export interface NavItem {
  to: string;
  label: string;
  permission: Permission | null;
}
